import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';

import { GetTagsWithHeadlines } from '@root/modules/tags/types/tag';
import getTagsWithHeadlines from '@root/modules/tags/graphql/getTagsWithHeadlines.graphql';

import { getRouteParams } from '@root/common/utils/url';
import { getPageOffset } from '@root/common/utils/getPageOffset';

type ServiceVariables = { slug?: string; page?: string; language?: string; tagLanguage?: string };

type FetchVariables = GetTagsWithHeadlines['variables'];

type Response = GetTagsWithHeadlines['data'];

export default class TagWithHeadlines extends Service implements ServiceInterface {
  private fixVariables({ slug, language, page, tagLanguage }: ServiceVariables) {
    const paramsString = getRouteParams({ slug, desiredIdType: 'string' });
    const limit = 30;
    const offset = getPageOffset(limit, page);
    const variables: FetchVariables = {
      offset,
      limit,
      slug: paramsString.slug,
      authorLanguage: language,
      channelLanguage: language,
      tagLanguage: tagLanguage,
    };
    return variables;
  }

  private async fetchTagWithHeadlines(variables: FetchVariables) {
    const apiProvider = this.createProvider('GraphQL');
    apiProvider
      .selectAPI('content-api-v3')
      .setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true })
      .setCacheOptions({ key: `fetchTagWithHeadlines:${JSON.stringify(variables)}`, ttl: 30 })
      .enableCache(true);

    const options = Object.assign({ query: getTagsWithHeadlines }, { variables });
    const response = await apiProvider.request<Response>(options);

    if (!response.tags || !(response.tags.data.length > 0)) {
      this.handleError();
    }

    const tag = response.tags.data[0];

    return {
      tag,
      headlines: response.headlines,
    };
  }

  public handleError() {
    throw {
      message: 'tag.error.not_found',
      statusCode: 404,
    };
  }

  public async fetch(variables: ServiceVariables) {
    const isHealthy = this.getHealthCheckStatus('content-api-v3');

    if (!isHealthy) {
      this.handleError();
    }

    const fixedVariables = this.fixVariables(variables);
    const response = await this.fetchTagWithHeadlines(fixedVariables);

    return response;
  }
}

export function fetchTagWithHeadlines(variables: ServiceVariables, { req }: { req: PortalRootIncomingMessage }) {
  const tagWithHeadlines = new TagWithHeadlines({ req });
  return tagWithHeadlines.fetch(variables);
}
